import { useEffect, useRef, useState } from "react";
import type { ChatBroadcastMessage, ChatItem, SystemMessage } from "@yay/shared";
import { ChatText } from "./ChatText";

interface FloatingActivityProps {
  messages: ChatItem[];
  chatOpen: boolean;
  theaterOpen: boolean;
  /** When true, suppress chat bubbles and join/leave popups (nerd has its own speech bubbles). */
  nerdMode?: boolean;
}

interface Bubble {
  id: number;
  username: string;
  color: string;
  text: string;
  fading: boolean;
}

interface Popup {
  id: number;
  text: string;
  fading: boolean;
}

let bubbleId = 0;
let popupId = 0;
let lockPopupId = 0;

function lockAnnouncementKey(msg: SystemMessage): string {
  return `${msg.timestamp}|${msg.text}`;
}

function markLockAnnouncementsSeen(
  messages: ChatItem[],
  seen: Set<string>,
): void {
  for (const msg of messages) {
    if (msg.type !== "system") continue;
    const sys = msg as SystemMessage;
    if (sys.kind !== "flash_lock") continue;
    seen.add(lockAnnouncementKey(sys));
  }
}

function schedulePopupFade(
  id: number,
  setPopups: React.Dispatch<React.SetStateAction<Popup[]>>,
) {
  setTimeout(() => {
    setPopups((prev) =>
      prev.map((p) => (p.id === id ? { ...p, fading: true } : p)),
    );
    setTimeout(() => {
      setPopups((prev) => prev.filter((p) => p.id !== id));
    }, 400);
  }, 4000);
}

export function FloatingActivity({
  messages,
  chatOpen,
  theaterOpen,
  nerdMode = false,
}: FloatingActivityProps) {
  const [bubbles, setBubbles] = useState<Bubble[]>([]);
  const [popups, setPopups] = useState<Popup[]>([]);
  const [lockPopups, setLockPopups] = useState<Popup[]>([]);

  const prevMsgLen = useRef(messages.length);
  const seenLockAnnouncements = useRef(new Set<string>());
  const lockPopupsReady = useRef(false);
  const joinLeaveReady = useRef(false);
  const seenJoinLeave = useRef(new Set<string>());

  // Chat bubbles — show when chat is collapsed (watch mode only)
  useEffect(() => {
    if (chatOpen || nerdMode) {
      prevMsgLen.current = messages.length;
      return;
    }
    const newMsgs = messages.slice(prevMsgLen.current);
    prevMsgLen.current = messages.length;

    for (const msg of newMsgs) {
      if (msg.type !== "chat") continue;
      const chatMsg = msg as ChatBroadcastMessage;
      const id = ++bubbleId;
      setBubbles((prev) => [
        ...prev.slice(-4),
        {
          id,
          username: chatMsg.username,
          color: chatMsg.color,
          text: chatMsg.text,
          fading: false,
        },
      ]);
      setTimeout(() => {
        setBubbles((prev) =>
          prev.map((b) => (b.id === id ? { ...b, fading: true } : b)),
        );
        setTimeout(() => {
          setBubbles((prev) => prev.filter((b) => b.id !== id));
        }, 300);
      }, 4000);
    }
  }, [messages, chatOpen, nerdMode]);

  // Join/leave popups — from chat system messages only. Presence flicker
  // (idle reap / AFK reconnect) must not toast "left" on its own.
  useEffect(() => {
    const seed = () => {
      for (const msg of messages) {
        if (msg.type !== "system") continue;
        if (!/ (joined|left)$/.test(msg.text)) continue;
        seenJoinLeave.current.add(`${msg.timestamp}|${msg.text}`);
      }
    };

    if (theaterOpen || nerdMode) {
      seed();
      return;
    }

    if (!joinLeaveReady.current) {
      seed();
      joinLeaveReady.current = true;
      return;
    }

    for (const msg of messages) {
      if (msg.type !== "system") continue;
      if (msg.kind) continue;
      if (!/ (joined|left)$/.test(msg.text)) continue;
      const key = `${msg.timestamp}|${msg.text}`;
      if (seenJoinLeave.current.has(key)) continue;
      seenJoinLeave.current.add(key);
      const id = ++popupId;
      setPopups((prev) => [...prev.slice(-4), { id, text: msg.text, fading: false }]);
      schedulePopupFade(id, setPopups);
    }
  }, [messages, theaterOpen, nerdMode]);

  // Lock/unlock flash-deck popups — nerd modes only, whenever chat is collapsed.
  useEffect(() => {
    if (chatOpen || !nerdMode) {
      markLockAnnouncementsSeen(messages, seenLockAnnouncements.current);
      return;
    }

    // First run: seed history without replaying old lock announcements.
    if (!lockPopupsReady.current) {
      markLockAnnouncementsSeen(messages, seenLockAnnouncements.current);
      lockPopupsReady.current = true;
      return;
    }

    for (const msg of messages) {
      if (msg.type !== "system") continue;
      const sys = msg as SystemMessage;
      if (sys.kind !== "flash_lock") continue;
      const key = lockAnnouncementKey(sys);
      if (seenLockAnnouncements.current.has(key)) continue;
      seenLockAnnouncements.current.add(key);
      const id = ++lockPopupId;
      setLockPopups((prev) => [
        ...prev.slice(-4),
        { id, text: sys.text, fading: false },
      ]);
      schedulePopupFade(id, setLockPopups);
    }
  }, [messages, chatOpen, nerdMode]);

  const showBubbles = !chatOpen && !nerdMode && bubbles.length > 0;
  const showPresencePopups =
    !theaterOpen && !nerdMode && popups.length > 0;
  const showLockPopups = !chatOpen && nerdMode && lockPopups.length > 0;

  if (!showBubbles && !showPresencePopups && !showLockPopups) return null;

  return (
    <>
      {showBubbles && (
        <div className="floating-bubbles">
          {bubbles.map((b) => (
            <div key={b.id} className={`floating-bubble${b.fading ? " fading" : ""}`}>
              <span className="floating-bubble-name" style={{ color: b.color }}>
                {b.username.split(" ")[0]}
              </span>
              <span className="floating-bubble-text">
                <ChatText text={b.text} compact stickersOnly />
              </span>
            </div>
          ))}
        </div>
      )}

      {showPresencePopups && (
        <div className="floating-popups">
          {popups.map((p) => (
            <div key={p.id} className={`floating-popup${p.fading ? " fading" : ""}`}>
              {p.text}
            </div>
          ))}
        </div>
      )}

      {showLockPopups && (
        <div className="floating-popups">
          {lockPopups.map((p) => (
            <div key={p.id} className={`floating-popup${p.fading ? " fading" : ""}`}>
              {p.text}
            </div>
          ))}
        </div>
      )}
    </>
  );
}
